import type { IThemeBranding } from "@frontend-shared/api";
import { useEffect } from "react";

import { apiClient } from "@/api/services";

import {
  applyOrgThemeCss,
  clearRuntimeBrandingStyleTags,
} from "./runtime-branding-css";
import type { IResolvedBranding } from "./types";

interface IUseApplyPublicBrandingProps {
  enabled: boolean;
  onBrandingResolved: (branding: IResolvedBranding) => void;
  onBrandingCleared: () => void;
}

// NOTE: NSAI-5315 — the unauthenticated counterpart to useApplyOrgBranding.
// /theme/me needs a session, so before sign-in the app knew nothing about the
// visiting organization's appearance: hasCustomBranding was always false, and
// the auth screens could not tell a default-appearance visitor from a branded
// one. Every screen the illustration rule covers (sign in, sign up, the
// password-recovery flow, the public shared-meeting gate) is pre-login, so
// without this the "hide it when a custom appearance is set" half of the rule
// could never fire.
//
// GET /theme/css is the public, cookie-resolved sibling of /theme/me that the
// logo already relies on (see getLogoUrl). Its resolution order is what makes
// this work where it matters: the signed branding cookie wins when present, and
// a single-org deployment — on-premise and white-label, exactly the ones that
// configure a custom appearance — falls back to the primary organization with no
// cookie at all. A first-time visitor to the multi-org SaaS legitimately
// resolves to the default, since no organization context exists yet.
export const useApplyPublicBranding = ({
  enabled,
  onBrandingResolved,
  onBrandingCleared,
}: IUseApplyPublicBrandingProps): void => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let isCancelled = false;
    const abortController = new AbortController();

    // NOTE: NSAI-6321 — two requests rather than one because the stylesheet and
    // the branding are two different media: /theme/css IS the CSS body, so the
    // platform name and the favicon flag cannot ride on it the way they ride on
    // /theme/me's JSON. Issued together so the pair still costs one round-trip's
    // worth of wall time.
    //
    // `allSettled`, NOT `all`: the two answer for different things and must fail
    // for different things. Coupling them made a failure of the newer branding
    // endpoint — a rolling deploy, a backend that predates it, one transient
    // 500 — discard a stylesheet that had arrived perfectly well and clear the
    // organization's appearance, so a white-label customer's login screen would
    // render in the platform's own colours because an unrelated lookup
    // hiccuped. Each half now degrades on its own.
    Promise.allSettled([
      // NOTE: text/css, not JSON — the endpoint streams the stylesheet itself,
      // so the response body IS the CSS (empty string when the organization is
      // on the default appearance).
      apiClient.get<string>("/theme/css", {
        signal: abortController.signal,
        responseType: "text",
      }),
      apiClient.get<IThemeBranding>("/theme/branding", {
        signal: abortController.signal,
      }),
    ]).then(([cssResult, brandingResult]) => {
      if (isCancelled) {
        return;
      }

      // NOTE: Both gone means there is nothing to say about this organization,
      // which is what `onBrandingCleared` is for: fall back, never blank.
      if (
        cssResult.status === "rejected" &&
        brandingResult.status === "rejected"
      ) {
        clearRuntimeBrandingStyleTags();
        onBrandingCleared();
        return;
      }

      // NOTE: A failed stylesheet is reported as an empty one — the same shape
      // an organization on the default appearance answers with — so the
      // illustration rule keeps treating it as "no custom branding" rather than
      // leaving the previous organization's CSS on the page.
      const cssContent =
        cssResult.status === "fulfilled" ? (cssResult.value.data ?? "") : "";

      if (cssResult.status === "rejected") {
        clearRuntimeBrandingStyleTags();
      } else {
        applyOrgThemeCss(cssContent);
      }

      const branding =
        brandingResult.status === "fulfilled"
          ? brandingResult.value.data
          : null;

      onBrandingResolved({
        cssContent,
        appName: branding?.appName ?? null,
        hasFavicon: !!branding?.hasFavicon,
        hasLogo: !!branding?.hasLogo,
      });
    });

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [enabled, onBrandingCleared, onBrandingResolved]);
};
